import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const INFLAMMATORY_TAGS = new Set([
  'gluten', 'dairy', 'refined_sugar', 'red_meat', 'alcohol',
  'caffeine', 'processed_food', 'soy', 'fried_food',
]);

export default function DietSummary({ meals }) {
  const summary = useMemo(() => {
    let inflammatory = 0;
    let antiInflammatory = 0;
    const tagCounts = {};

    for (const meal of meals) {
      if (!meal.foodTags) continue;
      for (const tag of meal.foodTags) {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        if (INFLAMMATORY_TAGS.has(tag)) {
          inflammatory++;
        } else {
          antiInflammatory++;
        }
      }
    }

    const topInflammatory = Object.entries(tagCounts)
      .filter(([tag]) => INFLAMMATORY_TAGS.has(tag))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    const topAntiInflammatory = Object.entries(tagCounts)
      .filter(([tag]) => !INFLAMMATORY_TAGS.has(tag))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return { inflammatory, antiInflammatory, topInflammatory, topAntiInflammatory };
  }, [meals]);

  const total = summary.inflammatory + summary.antiInflammatory;
  const pieData = total > 0 ? [
    { name: 'Anti-inflammatory', value: summary.antiInflammatory },
    { name: 'Inflammatory', value: summary.inflammatory },
  ] : [];

  return (
    <div className="glass rounded-xl p-6">
      <h3 className="text-sm font-semibold text-white mb-4">Diet Breakdown (30 days)</h3>

      {total === 0 ? (
        <p className="text-white/50 text-center py-8">No meal data yet</p>
      ) : (
        <div className="flex gap-4">
          <div className="w-32 h-32 flex-shrink-0">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={25} outerRadius={50} paddingAngle={2}>
                  <Cell fill="#ffffff" />
                  <Cell fill="#666666" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="flex-1 space-y-3">
            <div>
              <p className="text-xs font-semibold text-white/50 uppercase">Top Anti-inflammatory</p>
              {summary.topAntiInflammatory.map(([tag, count]) => (
                <p key={tag} className="text-sm text-white/80">
                  {tag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} <span className="text-white/40">({count}x)</span>
                </p>
              ))}
              {summary.topAntiInflammatory.length === 0 && (
                <p className="text-sm text-white/40">None logged</p>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold text-white/50 uppercase">Top Triggers</p>
              {summary.topInflammatory.map(([tag, count]) => (
                <p key={tag} className="text-sm text-white/80">
                  {tag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} <span className="text-white/40">({count}x)</span>
                </p>
              ))}
              {summary.topInflammatory.length === 0 && (
                <p className="text-sm text-white/40">None logged</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
